import type { Icon } from 'lucide-svelte';

import { browser } from '$app/environment';
import { SvelteMap } from 'svelte/reactivity';

interface Command {
	icon: typeof Icon;
	title: string;
	description?: string | undefined;
	keywords?: string[] | undefined;
	shortcut?: string[] | undefined;
	registerShortcut?: boolean | undefined;
	onSelect: () => Promise<void>;
}

interface CommandGroup {
	title: string;
	commands: SvelteMap<string, Command>;
}

interface CommandRegistration {
	unregister(): void;
}

const groupTitles = {
	chat: 'Chat',
	quick: 'Quick Access',
	navigation: 'Navigation',
	appearance: 'Appearance'
};

type GroupKey = keyof typeof groupTitles;

class CommandBar {
	groups = new SvelteMap<GroupKey, CommandGroup>();

	register(groupKey: GroupKey, commandKey: string, command: Command): CommandRegistration {
		const group = this.groups.get(groupKey) || {
			title: groupTitles[groupKey],
			commands: new SvelteMap()
		};
		group.commands.set(commandKey, command);
		this.groups.set(groupKey, group);

		let shortcut: ReturnType<typeof registerGlobalShortcut> | undefined;
		if (browser && command.registerShortcut && command.shortcut) {
			shortcut = registerGlobalShortcut(command.shortcut, command.onSelect);
		}

		return {
			unregister: () => {
				shortcut?.unregister();

				const group = this.groups.get(groupKey) || {
					title: groupTitles[groupKey],
					commands: new SvelteMap()
				};
				group.commands.delete(commandKey);

				if (group.commands.size === 0) {
					this.groups.delete(groupKey);
				} else {
					this.groups.set(groupKey, group);
				}
			}
		};
	}
}

function registerGlobalShortcut(keys: string[], onSelectHandler: () => Promise<void>) {
	const lowerKeys = keys.map((key) => key.toLowerCase());

	const hasCtrl = lowerKeys.includes('⌘') || lowerKeys.includes('ctrl');
	const hasShift = lowerKeys.includes('⇧') || lowerKeys.includes('shift');
	const hasAlt = lowerKeys.includes('⌥') || lowerKeys.includes('alt');

	const listener = (evt: KeyboardEvent) => {
		if (
			evt.ctrlKey === hasCtrl &&
			evt.shiftKey === hasShift &&
			evt.altKey === hasAlt &&
			lowerKeys.includes(evt.key)
		) {
			evt.preventDefault();
			onSelectHandler();
		}
	};

	globalThis.addEventListener('keydown', listener);

	return {
		unregister: () => {
			globalThis.removeEventListener('keydown', listener);
		}
	};
}

export const commandBar = new CommandBar();
